import { useField } from 'formik'
import { MdContentCopy, MdDelete } from 'react-icons/md'

import type { ReactElement, ChangeEvent } from 'react'

import Button from '~/components/Buttons/Button'
import LinkButton from '~/components/Buttons/LinkButton'
import CustomVariationControl from '~/components/CustomVariationControl'
import SelectInput from '~/components/Inputs/Formik/SelectInput'
import TextInput from '~/components/Inputs/Formik/TextInput'
import Label from '~/components/Label'
import Slider from '~/components/Slider'
import { copyText } from '~/utils/navigator'
import { reactSelectMenuPositionDef } from '~/utils/options'

import './styles.scss'

interface Props {
  modalId: string
  name: string
  navigationGraphOptions: IOption[]
  onRemove?: VoidFunction
  variationType: 'control' | 'variation'
}

const MAX_VALUE = 100
const MIN_VALUE = 0

const formatValue = (value: string): number => {
  const parsedNumber = Number(value)
  return parsedNumber > MAX_VALUE ? MAX_VALUE : parsedNumber < MIN_VALUE ? MIN_VALUE : parsedNumber
}

const VariationCard = ({ modalId, name, navigationGraphOptions, onRemove, variationType }: Props): ReactElement => {
  const [, { value: trafficShare }, { setValue }] = useField<number | undefined>(`${name}.trafficShare`)

  const [, { value: navigationGraphId }] = useField<IOption | undefined>(`${name}.id`)

  const handleSliderChange = (value: number) => {
    setValue(value)
  }

  const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target
    setValue(value ? Number(value) : undefined)
  }

  const handleInputBlur = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target
    setValue(value ? formatValue(value) : 0)
  }

  const handleNavigationGraphLinkClick = () => {
    const id = navigationGraphId?.value
    id && window.open(`/dynamic-screens/navigation-graphs/${id}`, '_blank')
  }

  return (
    <div className="pool-variation-card">
      <div className="pool-variation-card__left">
        <div className="pool-variation-card__left__nav-graph">
          <SelectInput
            {...reactSelectMenuPositionDef(modalId)}
            components={{ Control: CustomVariationControl }}
            label="Navigation graph"
            name={`${name}.id`}
            options={navigationGraphOptions}
            variationType={variationType}
          />
          {navigationGraphId ? (
            <div className="pool-variation-card__left__nav-graph__id">
              <LinkButton
                backgroundColor="light"
                className="nav-graph-id-link"
                label={navigationGraphId?.value}
                onClick={handleNavigationGraphLinkClick}
                type="external"
              />
              <Button
                ghost
                onClick={() => copyText(navigationGraphId.value, 'Navigation graph ID copied to clipboard.')}
                size="small"
                type="button"
              >
                <MdContentCopy size={14} />
              </Button>
            </div>
          ) : null}
          {}
        </div>
      </div>
      <div className="pool-variation-card__right">
        {onRemove ? (
          <div className="variation-controls">
            <Button ghost onClick={onRemove} type="button">
              <MdDelete size={16} />
            </Button>
          </div>
        ) : null}
        <div className="variation-traffic">
          <Label label="Traffic share" />
          <div className="traffic-share">
            <Slider className="traffic-share__slider" onChange={handleSliderChange} value={trafficShare || 0} />
            <div className="traffic-share__value">
              <TextInput
                componentClassName="traffic-share-input"
                max={MAX_VALUE}
                min={MIN_VALUE}
                name={`${name}.trafficShare`}
                onBlur={handleInputBlur}
                onChange={handleInputChange}
                type="number"
                value={trafficShare || 0}
              />
              <div className="traffic-share__value__unit">%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VariationCard
